/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React, { useEffect, ReactElement } from "react";

import { useDispatch, useSelector } from "react-redux";

import _ from "lodash";

import { Label, Spinner, SpinnerSize, Stack, StackItem, Text } from "@fluentui/react";

import {
  bodyContainerStackStyles,
  bodyContainerStackTokens,
  headerStackStyles,
  headerTextStyles,
  labelStyles,
  ContainerWrapper,
} from "Components/Common/SharedTableStyles.styles";
import { TenantTypesEnum } from "Constants/TenantTypes";
import { fetchApplicationQuestionsBySelectedApplicationId } from "Dataflows/ApplicationDetails/ApplicationDetailsAction";
import {
  getApplicationQuestionsBySelectedApplicationId,
  getApplicationQuestionsBySelectedApplicationIdErrorMessage,
  getIsLoadingApplicationQuestionsBySelectedApplicationId,
  getUserProfileInfoBySelectedApplicationId,
} from "Dataflows/ApplicationDetails/ApplicationDetailsSelector";
import { IApplicationQuestionResponse } from "Interfaces/Application/IApplicationQuestionResponse";
import { GetApplicationQuestionsGroupBySectionAndByQuestionId } from "Utility/Utils";

const ApplicationQuestions = (): ReactElement => {
  const dispatch = useDispatch();
  const applicationQuestions: IApplicationQuestionResponse[] | undefined = useSelector(
    getApplicationQuestionsBySelectedApplicationId
  );
  const errorMessage = useSelector(getApplicationQuestionsBySelectedApplicationIdErrorMessage);
  const isLoading = useSelector(getIsLoadingApplicationQuestionsBySelectedApplicationId);
  const userProfileInfo = useSelector(getUserProfileInfoBySelectedApplicationId);
  const applicationQuestionsGroupBySectionAndByQuestionId =
    GetApplicationQuestionsGroupBySectionAndByQuestionId(applicationQuestions);

  useEffect(() => {
    if (!applicationQuestions && !isLoading && !!userProfileInfo?.applicationFormId) {
      dispatch(
        fetchApplicationQuestionsBySelectedApplicationId(
          userProfileInfo.applicationFormId,
          TenantTypesEnum.MVP
        )
      );
    }
  }, [applicationQuestions, dispatch, isLoading]);

  return !!isLoading ? (
    <Spinner size={SpinnerSize.large} />
  ) : !!errorMessage ? (
    <p>{errorMessage}</p>
  ) : (
    <Stack>
      <ContainerWrapper>
        <Stack styles={headerStackStyles}>
          <Text styles={headerTextStyles}>Technology Area</Text>
        </Stack>
        <Stack styles={bodyContainerStackStyles} tokens={bodyContainerStackTokens}>
          {applicationQuestionsGroupBySectionAndByQuestionId["Technology Area"]?.map((question) => (
            <StackItem key={question.questionId}>
              <Label styles={labelStyles}>{question.questionText}</Label>
              <Text>{question.questionResponseText}</Text>
            </StackItem>
          ))}
        </Stack>
      </ContainerWrapper>
      <ContainerWrapper>
        <Stack styles={headerStackStyles}>
          <Text styles={headerTextStyles}>Application Questions</Text>
        </Stack>
        <Stack horizontal wrap styles={bodyContainerStackStyles} tokens={bodyContainerStackTokens}>
          {applicationQuestionsGroupBySectionAndByQuestionId["Application Questions"]
            ?.map((question) => (
              <StackItem
                key={question.questionId}
                grow={question.questionLocKey === "MVP_APP_WHY_BE_MVP" ? 2 : 1}
                styles={{
                  root: {
                    width: question.questionLocKey === "MVP_APP_WHY_BE_MVP" ? "100%" : "40%",
                  },
                }}
              >
                <Label styles={labelStyles}>{question.questionText}</Label>
                <Text>{question.questionResponseText}</Text>
              </StackItem>
            ))
            // Sort by grow, so that MVP_APP_WHY_BE_MVP in effect is always last.
            .sort((a, b) => {
              if (a.props.grow === b.props.grow) {
                return 0;
              }
              return a.props.grow < b.props.grow ? -1 : 1;
            })}
        </Stack>
      </ContainerWrapper>
      <ContainerWrapper>
        <Stack styles={headerStackStyles}>
          <Text styles={headerTextStyles}>Technical Expertise</Text>
        </Stack>
        <Stack styles={bodyContainerStackStyles} tokens={bodyContainerStackTokens}>
          {applicationQuestionsGroupBySectionAndByQuestionId["Technical Expertise"]?.map(
            (question) => (
              <StackItem key={question.questionId}>
                <Label styles={labelStyles}>{question.questionText}</Label>
                <Text>{question.questionResponseText}</Text>
              </StackItem>
            )
          )}
        </Stack>
      </ContainerWrapper>
    </Stack>
  );
};

export default ApplicationQuestions;
